/* eslint-disable @typescript-eslint/no-explicit-any */
import { Menu, Sun, Moon, User, LogOut, HelpCircle, Languages, Route, BookOpen, Settings2, ChevronRight, Info, AlertCircle, Building2 } from "lucide-react";
import { NotificationsBell } from "./NotificationsBell";
import { Button } from "@/shared/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/shared/components/ui/select";
import { cn } from "@/infrastructure/lib/utils";
import { useProfileTheme } from "@/shared/hooks/use-profile-theme";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useToast } from "@/shared/hooks/use-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { useAvatar } from "@/features/Profile/state/AvatarContext";
import { useIsAdmin } from "@/shared/hooks/use-admin-validation";
import { WorkspaceButton } from "@/features/Workspaces/components/WorkspaceButton";
import { useTour } from "@/features/Tour";
import { useViewScope } from "@/shared/hooks/use-view-scope";
import { useEffect, useState, useMemo } from "react";
import { UserSettingsService } from "@/features/Users/services/userSettingsService";
import { UserRole } from "@/shared/types/user-role";
import { CreditCounter } from "./CreditCounter";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import { getBuildInfo } from "@/infrastructure/config/version";
import { useVersion } from "@/shared/hooks/api/useVersion";
import { logger } from "@/infrastructure/lib/logger";

// Sentinel de Select para "voltar à visão da organização" (UUIDs reais nunca colidem com este padrão)
const ORG_VIEW_VALUE = "__org_view__";

// Função para salvar preferência de idioma no localStorage
const saveLanguagePreference = (language: string): void => {
  try {
    localStorage.setItem('prodgy-user-language-preference', language);
  } catch (e) {
    logger.error('Failed to save language preference in localStorage', { error: (e as any)?.message });
  }
};

interface NavbarProps {
  onMenuClick: () => void;
  isOnboarding?: boolean;
  /** Sem sidebar/workspace/créditos — usuário sem organização (NoOrganizationScreen) */
  restricted?: boolean;
}

export const Navbar = ({ onMenuClick, isOnboarding = false, restricted = false }: NavbarProps) => {
  const { theme, toggleTheme } = useProfileTheme();
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { currentWorkspace, setCurrentWorkspace, products, isLoading: isLoadingProducts } = useWorkspace();
  const { avatarUrl } = useAvatar();
  const { isAdmin } = useIsAdmin();
  const { scope } = useViewScope();
  const tourContext = useTour();
  const restartTour = tourContext?.restartTour;
  const [showCreditCounter, setShowCreditCounter] = useState(() => {
    const stored = localStorage.getItem('prodgy-show-credit-counter');
    return stored === null ? true : stored === 'true';
  });
  const [aboutOpen, setAboutOpen] = useState(false);
  const { data: versionInfo } = useVersion({ enabled: !isOnboarding });
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);

  const pageTitle = useMemo(() => {
    const path = location.pathname;
    if (path === '/' || path === '') return t("navigation.dashboard");
    if (path.startsWith('/knowledge')) return t("navigation.knowledge");
    if (path.startsWith('/agents')) return t("navigation.agents");
    if (path.startsWith('/playground')) return t("navigation.playground");
    if (path.startsWith('/integrations')) return t("navigation.integrations");
    if (path.startsWith('/profile')) return t("navigation.profile");
    if (path.startsWith('/admin/queues')) return t("settings.tabs.queues", "Queues");
    if (path.startsWith('/admin/templates')) return t("settings.tabs.templates", "Templates");
    if (path.startsWith('/admin/logs')) return t("settings.tabs.logs");
    if (path.startsWith('/admin/feedback')) return t("settings.tabs.feedback");
    if (path.startsWith('/admin/email')) return t("settings.sidebar.email");
    if (path.startsWith('/admin/plans')) return t("settings.tabs.plans", "Plans");
    if (path.startsWith('/settings')) return t("navigation.generalSettings");
    if (path.startsWith('/products') && path.includes('/config')) return t("navigation.workspaceSettings");
    return null;
  }, [location.pathname, t]);

  // Listen to storage changes for credit counter visibility
  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('prodgy-show-credit-counter');
      setShowCreditCounter(stored === null ? true : stored === 'true');
    };

    window.addEventListener('storage', handleStorageChange);
    // Also listen for custom event for same-tab updates
    window.addEventListener('credit-counter-toggle', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('credit-counter-toggle', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (user) {
      // Corrigir a role do usuário se houver inconsistência
      // Prioridade: 1. role do JWT, 2. role dos metadados
      if (user.role && user.user_metadata?.role && user.role !== user.user_metadata.role) {
        logger.warn('User role inconsistency detected', {
          jwtRole: user.role,
          metadataRole: user.user_metadata.role
        });

        // Atualizamos a role nos metadados para corresponder à role do JWT
        const updatedUser = {
          ...user,
          user_metadata: {
            ...user.user_metadata,
            role: user.role // Usar a role do JWT (que vem do backend)
          }
        };

        // Não podemos modificar o user diretamente pois é gerenciado pelo contexto
        // Mas podemos logar as informações corretas
        logger.info('User data normalized (role taken from JWT)', { user: updatedUser });
      } else {
        logger.debug('User data loaded', { user });
      }
    }
  }, [user]);

  const displayAvatarUrl = avatarUrl ||
    user?.user_metadata?.avatar_url ||
    (user as any)?.avatarUrl ||
    user?.user_metadata?.avatarUrl ||
    null;

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: t("auth.signedOut"),
      });
    } catch (error) {
      logger.error('Error signing out', { error: (error as any)?.message });
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("auth.signInError"),
      });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 h-16 glass-effect z-50">
      <div className="flex items-center justify-between h-full px-4">
        <div className="flex items-center gap-4">
          {!isOnboarding && !restricted && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onMenuClick}
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}

          {!isOnboarding && !restricted && scope !== 'platform' && !isLoadingProducts && (products.length === 0 || isCreatingProduct) && isAdmin ? (
            <WorkspaceButton onCreationStateChange={setIsCreatingProduct} />
          ) : !isOnboarding && !restricted && scope !== 'platform' && !isLoadingProducts && products.length > 0 ? (
            <div className="relative flex items-center gap-1">
              <Select
                value={currentWorkspace?.id || (isAdmin ? ORG_VIEW_VALUE : "")}
                onValueChange={(value) => {
                  const currentPathBeforeChange = window.location.pathname;
                  if (value === ORG_VIEW_VALUE && isAdmin) {
                    setCurrentWorkspace(null);
                    if (currentPathBeforeChange.includes('/products/') && currentPathBeforeChange.includes('/config')) {
                      navigate('/');
                    }
                    return;
                  }
                  const workspace = products.find(p => p.id === value);
                  if (workspace) {
                    setCurrentWorkspace(workspace);
                    if (currentPathBeforeChange.includes('/products/') && currentPathBeforeChange.includes('/config')) {
                      navigate(`/products/${workspace.id}/config`);
                    } else if (currentPathBeforeChange === '/' || currentPathBeforeChange === '') {
                      navigate('/');
                    }
                  }
                }}
              >
                <SelectTrigger className={cn("w-[200px] lg:w-[240px]")}>
                  <div className="flex items-center w-full min-w-0">
                    {currentWorkspace ? (
                      <>
                        <Avatar className="h-6 w-6 mr-2 flex-shrink-0">
                          <AvatarImage
                            src={currentWorkspace.logo_url || ''}
                            alt={currentWorkspace.name || ''}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              if (target) target.src = '';
                            }}
                          />
                          <AvatarFallback>{(currentWorkspace.name || '')?.[0].toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <span className="truncate">{currentWorkspace.name}</span>
                      </>
                    ) : isAdmin ? (
                      <div className="flex items-center min-w-0">
                        <Building2 className="h-4 w-4 mr-2 flex-shrink-0 text-muted-foreground" />
                        <span className="text-muted-foreground truncate">{t("navigation.organizationView", "Visão da organização")}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground truncate">{t("navigation.selectWorkspace")}</span>
                    )}
                  </div>
                  {currentWorkspace?.id && (
                    <span
                      role="button"
                      tabIndex={-1}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        navigate(`/products/${currentWorkspace.id}/config`);
                      }}
                      title={t('settings.workspaces.title')}
                      className="flex-shrink-0 mr-1.5 p-1 rounded-sm hover:bg-accent transition-colors focus:outline-none cursor-pointer"
                    >
                      <Settings2 className="h-4 w-4 opacity-50 hover:opacity-100" />
                    </span>
                  )}
                </SelectTrigger>
                <SelectContent>
                  {isAdmin && (
                    <SelectItem value={ORG_VIEW_VALUE}>
                      <div className="flex items-center gap-2">
                        <div className="h-6 w-6 flex items-center justify-center rounded bg-muted text-muted-foreground">
                          <Building2 className="h-3.5 w-3.5" />
                        </div>
                        <span className="truncate">{t("navigation.organizationView", "Visão da organização")}</span>
                      </div>
                    </SelectItem>
                  )}
                  {products.map((workspace) => (
                    <SelectItem key={workspace.id} value={workspace.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage
                            src={workspace.logo_url || ''}
                            alt={workspace.name || ''}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              if (target) target.src = '';
                            }}
                          />
                          <AvatarFallback>{(workspace.name || '')?.[0].toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <span className="truncate">{workspace.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {!isOnboarding && pageTitle && (
            <div className="hidden md:flex items-center ml-1 gap-2 text-muted-foreground">
              {scope === 'platform' && user?.role?.toLowerCase() === UserRole.SUPERADMIN && (
                <>
                  <span className="text-base font-medium text-foreground">
                    {t("navigation.platformView")}
                  </span>
                </>
              )}
              <ChevronRight className="h-4 w-4" />
              <h1 className="text-xl font-semibold tracking-tight text-foreground">{pageTitle}</h1>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 lg:gap-4">
          {!isOnboarding && !restricted && showCreditCounter && <CreditCounter />}

          {!isOnboarding && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  title={t("common.help_center")}
                  className="focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-tour-help-button
                >
                  <HelpCircle className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={5} className="w-56">
                <DropdownMenuItem
                  onClick={() => window.open(
                    (window.ENV?.VITE_PRODGY_DOCS_URL || import.meta.env.VITE_PRODGY_DOCS_URL || '#'),
                    '_blank'
                  )}
                >
                  <BookOpen className="mr-2 h-4 w-4" />
                  <span>{t("common.help.userManual")}</span>
                </DropdownMenuItem>
                {user?.role !== UserRole.USER && (
                  <DropdownMenuItem onClick={restartTour} disabled={!restartTour}>
                    <Route className="mr-2 h-4 w-4" />
                    <span>{t("common.help.guidedTour")}</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setAboutOpen(true)}>
                  <Info className="mr-2 h-4 w-4" />
                  <span>{t("common.help.about", "Sobre o Prodgy")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === "dark" ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>

          {/* Botão de ajuda no onboarding - leva direto ao manual */}
          {isOnboarding && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => window.open(
                (window.ENV?.VITE_PRODGY_DOCS_URL || import.meta.env.VITE_PRODGY_DOCS_URL || '#'),
                '_blank'
              )}
              title={t("common.help.userManual")}
            >
              <HelpCircle className="h-5 w-5" />
            </Button>
          )}

          {!isOnboarding && <NotificationsBell />}

          {isOnboarding ? (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <Languages className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={async () => {
                    await i18n.changeLanguage('pt');
                    saveLanguagePreference('pt');
                    if (user?.id) {
                      try {
                        await UserSettingsService.updateByUserId(user.id, { language: 'pt' });
                      } catch (error) {
                        logger.error('Failed to save language in backend', { error: (error as any)?.message });
                      }
                    }
                  }}
                  className={i18n.language.split('-')[0] === 'pt' ? 'bg-accent' : ''}
                >
                  <span className="mr-2">🇧🇷</span>
                  <span>Português</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    await i18n.changeLanguage('en');
                    saveLanguagePreference('en');
                    if (user?.id) {
                      try {
                        await UserSettingsService.updateByUserId(user.id, { language: 'en' });
                      } catch (error) {
                        logger.error('Failed to save language in backend', { error: (error as any)?.message });
                      }
                    }
                  }}
                  className={i18n.language.split('-')[0] === 'en' ? 'bg-accent' : ''}
                >
                  <span className="mr-2">🇺🇸</span>
                  <span>English</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={displayAvatarUrl || undefined} alt={user?.user_metadata?.name || user?.email} />
                    <AvatarFallback>{(user?.user_metadata?.name?.[0] || user?.email?.[0])?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user?.user_metadata?.full_name}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <User className="mr-2 h-4 w-4" />
                  <span>{t("navigation.profile")}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t("auth.signOut")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* About Prodgy Modal */}
      <Dialog open={aboutOpen} onOpenChange={setAboutOpen}>
        <DialogContent className="sm:max-w-sm p-0 gap-0 overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-8 pb-4 text-center">
            <img
              src="/assets/logo.png"
              alt="Prodgy"
              className="h-10 mx-auto mb-3 dark:brightness-0 dark:invert"
            />
            <p className="text-sm text-muted-foreground">
              The AI Company OS
            </p>
          </div>

          {/* Version info */}
          <div className="px-6 py-5 space-y-3">
            {(() => {
              const buildInfo = getBuildInfo();
              const frontendVersion = buildInfo.version?.trim();
              const backendVersion = versionInfo?.version?.trim();
              const versionsMatch = backendVersion ? frontendVersion === backendVersion : true;
              const buildDate = versionInfo?.buildDate || buildInfo.buildDate;
              const formattedDate = buildDate
                ? new Date(buildDate).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' })
                : null;

              return (
                <>
                  <div className="grid grid-cols-2 gap-y-2.5 text-sm">
                    <span className="text-muted-foreground">{t("common.help.version", "Versão")}</span>
                    <span className="text-right font-mono font-medium">
                      {backendVersion || frontendVersion}
                    </span>

                    {!versionsMatch && (
                      <>
                        <span className="text-muted-foreground">Frontend</span>
                        <span className="text-right font-mono text-xs">{frontendVersion}</span>
                        <span className="text-muted-foreground">Backend</span>
                        <span className="text-right font-mono text-xs">{backendVersion}</span>
                      </>
                    )}

                    {versionInfo?.environment && (
                      <>
                        <span className="text-muted-foreground">{t("common.help.environment", "Ambiente")}</span>
                        <span className="text-right capitalize">{versionInfo.environment}</span>
                      </>
                    )}

                    {formattedDate && (
                      <>
                        <span className="text-muted-foreground">{t("common.help.lastUpdate", "Atualização")}</span>
                        <span className="text-right">{formattedDate}</span>
                      </>
                    )}
                  </div>

                  {!versionsMatch && (
                    <div className="flex items-center gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-600 dark:text-red-400">
                      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>{t("common.help.versionMismatch", "Versões incompatíveis entre frontend e backend")}</span>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-4 bg-muted/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">
                  {t("common.help.madeBy", "Um produto")} <span className="font-medium text-foreground">Programmers Beyond IT</span>
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  &copy; {new Date().getFullYear()} Prodgy &mdash; {t("common.help.allRightsReserved", "Todos os direitos reservados")}
                </p>
              </div>
              <a
                href="https://prodgy.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                prodgy.ai
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </nav>
  );
};
